import { Component, OnInit, ViewChild, Renderer2 } from '@angular/core';
import { Location } from '@angular/common';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { matchingPasswords, passwordPrerequisites } from 'src/app/shared/validators/password.validator';
import { alphaNumericValidator } from 'src/app/shared/validators/alpha-numeric.validator';
import { fullNameValidator } from 'src/app/shared/validators/fullname.validator';
import { SnackbarComponent } from 'src/app/shared/snackbar/snackbar.component';
import { TranslationConstants } from 'src/app/shared/services/util/translation.constants';
import { SnackBarTheme } from 'src/app/shared/models/snackbar-theme.enum';
import { Router } from '@angular/router';
import { AccountService } from 'src/app/shared/services/account/account.services';
import { CpfCnpjValidator } from './../../shared/validators/cpf-cnpj.validator';
import * as Sentry from "@sentry/angular";
import { ConfigReaderService } from 'src/app/shared/services/util/config.reader.service';
import { UserIdentifierEnum } from 'src/app/shared/models/user-identifier.enum';
import { ExternalDocumentValidator } from 'src/app/shared/validators/external.document.validator';
import { FeaturesStatusService } from 'src/app/shared/services/util/features-status.service';
import { FeatureNames } from 'src/app/shared/models/feature-names.enum';
import { FileService } from 'src/app/shared/services/util/file.service';
import { OnkeypressService } from 'src/app/shared/services/util/onkeypress.service';

@Component({
    selector: 'app-signup',
    templateUrl: './signup.component.html',
    styleUrls: ['../styles/card.style.component.scss', './signup.component.scss']
})
export class SignUpComponent implements OnInit {

    public loading: boolean = false;
    public loadingData: boolean = false;
    public documentAlreadyRegistered: boolean = false;
    public showPassword: boolean = false;
    public hasMinChars: boolean = false;
    public hasLowerCase: boolean = false;
    public hasUpperCase: boolean = false;
    public hasNumber: boolean = false;
    public hasSpecialChar: boolean = false;
    public hideVisiblePwd: boolean = true;
    public hideVisibleConfirmPwd: boolean = true;
    public showPrerequisites: boolean = true;
    public match: boolean = true;
    public showNewPassword: boolean = false;
    public showRepeatedPassword: boolean = false;
    public accept: boolean = false;
    public isBrazilian: boolean = true;
    public isBrazilianCheck: boolean = true;
    public termsOfUseURL: string = '';
    public termsOfUseLoaded: boolean = false;

    @ViewChild('stepper') public stepper: any;

    public terms: FormGroup = this._formBuilder.group({
        accept: ['', [Validators.requiredTrue]]
    });

    public personalData: FormGroup = this._formBuilder.group({
        name: ['', [Validators.required, alphaNumericValidator, fullNameValidator]],
        cpg: ['', [Validators.required, CpfCnpjValidator]],
        person: [true],
        isBrazilian: [true]
    });

    public userData: FormGroup = this._formBuilder.group({
        email: ['', [Validators.required, Validators.email]],
        password: ['', [Validators.required, Validators.minLength(8), Validators.maxLength(20), passwordPrerequisites]],
        confirmPassword: ['', [Validators.required, Validators.minLength(8), Validators.maxLength(20)]]
    }, [matchingPasswords('password', 'confirmPassword')]);

    constructor(
        private readonly accountService: AccountService,
        private readonly location: Location,
        private readonly _formBuilder: FormBuilder,
        private readonly snackbarComponent: SnackbarComponent,
        private readonly translationConstants: TranslationConstants,
        private readonly featureStatusService: FeaturesStatusService,
        private readonly router: Router,
        private readonly renderer: Renderer2,
        private readonly myConfigReaderService: ConfigReaderService,
        private readonly fileService: FileService,
        private readonly onKeyPressService: OnkeypressService
    ) { }

    public ngOnInit(): void {
        this.accountService.getRsaPublicKey().subscribe();
        this.myConfigReaderService.getAllExternalLinks().subscribe(data => {
            if (data?.externalFiles?.termsOfUse){
                this.termsOfUseURL = data?.externalFiles?.termsOfUse;
            }
        });
    }

    public ngAfterViewChecked(): void{
        if (!this.termsOfUseLoaded){
            let iFrameElement: HTMLIFrameElement = document.getElementById("termsOfUseIFrame") as HTMLIFrameElement;
            if (iFrameElement){
                iFrameElement.src = this.termsOfUseURL;
                this.termsOfUseLoaded = true;
            }
        }
    }

    public acceptTerms(element: any): void {
        this.accept = !this.accept;

        setTimeout(() => {
            this.terms.patchValue({ accept: this.accept });
            if (!this.accept) {
                element.checked = false;
                this.renderer.removeClass(element['_elementRef'].nativeElement, 'cdk-focused');
                this.renderer.removeClass(element['_elementRef'].nativeElement, 'cdk-program-focused');
            }
        });
    }

    public clearInputCpg(): void {
        this.personalData.patchValue({cpg: ''});
        if (this.isBrazilianCheck) {
            this.personalData.controls.cpg.clearValidators();
            this.personalData.controls.cpg.setValidators([Validators.required, CpfCnpjValidator.validate]);
        } else {
            this.personalData.controls.cpg.clearValidators();
            this.personalData.controls.cpg.setValidators([Validators.required, ExternalDocumentValidator.validate]);
        }
    }

    private defineDocumentType(): UserIdentifierEnum {
        if (!this.isBrazilianCheck) {
            return UserIdentifierEnum.External;
        } else if (this.personalData.value.cpg.length === 11) {
            return UserIdentifierEnum.CPF;
        } else if (this.personalData.value.cpg.length === 14) {
            return UserIdentifierEnum.CNPJ;
        }
    }

    public signUp(): void {
        this.loading = true;
        const firstName = this.personalData.value.name.split(' ')[0];
        const auxLastName = this.personalData.value.name.split(' ');
        let lastName: string = '';
        auxLastName.forEach( (name: string, index: number) => {
            if (index === 1) {
                lastName += `${name}`;
            } else if (index > 1) {
                lastName += ` ${name}`;
            }
        });
        Sentry.setUser({ email: this.userData.value.email });
        this.accountService.createUser(this.userData.value.email, this.userData.value.password, firstName, lastName, this.personalData.value.cpg, this.defineDocumentType()).subscribe(() => {
            this.snackbarComponent.openSnackBar(this.translationConstants.translate('account.signUp.snackbar.success'), SnackBarTheme.success, 8000);
            this.router.navigate(['account/login']);
        }, error => {
            if(error?.error?.message?.includes('User already exists')) {
                this.snackbarComponent.openSnackBar(this.translationConstants.translate('account.signUp.error.existingAccount'), SnackBarTheme.error, 8000);
            } else if(error?.error?.message?.includes('Document already exists')) {
                this.snackbarComponent.openSnackBar(this.translationConstants.translate('account.signUp.error.existingDocument'), SnackBarTheme.error, 8000);
            } else {
                this.snackbarComponent.openSnackBar(error?.error?.message, SnackBarTheme.error, 8000);
            }
        }).add(() => {
            this.loading = false;
        });
    }

    public cancel(): void {
        this.location.back();
    }

    /**
     * To validate confirm password input
     */
     public matchPass(): any {
        this.match = this.userData.value.password === this.userData.value.confirmPassword;
    }

    /**
     * Validate password prerequisites to show on screen
     * @param password
     */
    public validatePassword(): any {

        if (this.showPrerequisites) {
            const password = this.userData.value.password;
            this.hasMinChars = false;
            this.hasLowerCase = false;
            this.hasUpperCase = false;
            this.hasNumber = false;
            this.hasSpecialChar = false;

            if (password.length > 7) {
                this.hasMinChars = true;
            }

            const lowerCaseLetters = /[a-z]/g;
            if (password.match(lowerCaseLetters)) {
                this.hasLowerCase = true;
            }

            const upperCaseLetters = /[A-Z]/g;
            if (password.match(upperCaseLetters)) {
                this.hasUpperCase = true;
            }

            const numbers = /[0-9]/g;
            if (password.match(numbers)) {
                this.hasNumber = true;
            }

            const specialCharacter = /[-!@#$%^*()_+.]/g;
            if (password.match(specialCharacter) && password.length !== 0) {
                this.hasSpecialChar = true;
            }
        }

        if (this.userData.value.confirmPassword) {
            this.matchPass();
        }
    }

    public onDrop(event: any): void {
        event.preventDefault();
    }

    public verifyDocument(): void {
        this.loadingData = true;
        if (this.personalData.value.cpg) {
            if (!this.isBrazilianCheck) {
                this.documentAlreadyRegistered = false;
                this.stepper.next();
            } else {
                const cpf = this.personalData.value.cpg.length === 11 ? this.personalData.value.cpg : undefined;
                const cnpj = this.personalData.value.cpg.length === 14 ? this.personalData.value.cpg : undefined;

                this.accountService.verifyDocument(cpf, cnpj).subscribe(documentFound => {
                    if (documentFound["value"]) {
                        this.documentAlreadyRegistered = true;
                    } else {
                        this.documentAlreadyRegistered = false;
                        this.stepper.next();
                    }
                }, error => {
                    this.loadingData = false;
                }).add(() => {
                    this.loadingData = false;
                })
            }
        }
        this.loadingData = false;
    }

    public blockSpecialCharacters(event: any): void {
        this.onKeyPressService.blockSpecialCharacters(event);
    }

    public toDisabledForwardStepperButton(): boolean {
        const dataValidation: boolean = this.personalData.invalid || !this.accept || this.loading;
        const externalDocValidation: boolean = this.personalData.invalid;

        if (this.isBrazilianCheck) {
            return dataValidation;
        } else {
            return externalDocValidation;
        }
    }
}
